import React, { useState, useRef } from 'react';
import { Card, Typography, Divider, Button, Input, message } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    PointerSensor,
    KeyboardSensor,
    DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    sortableKeyboardCoordinates,
    arrayMove
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

import { AttractorImageDto } from '../../types/attractortypes';
import SortableSlideItem from './sortableslideitem';

const { Title, Paragraph } = Typography;

const generateTempId = () => {
    return `temp-${Math.random().toString(36).substring(2, 11)}`;
};

const checkResolution = (base64String: string): Promise<{ width: number; height: number; isValid: boolean }> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.src = base64String;
        img.onload = () => {
            const width = img.width;
            const height = img.height;
            const isValid = (width === 1080 && height === 1920) || (width === 1080 && height === 1300);
            resolve({ width, height, isValid });
        };
        img.onerror = () => {
            resolve({ width: 0, height: 0, isValid: false });
        };
    });
};

export interface AttractorSlideManagerProps {
    images: AttractorImageDto[];
    onChange: (images: AttractorImageDto[]) => void;
    getUserName: (guid: string) => string;
    currentUserGuid: string;
}

const AttractorSlideManager: React.FC<AttractorSlideManagerProps> = ({
    images,
    onChange,
    getUserName,
    currentUserGuid
}) => {
    const [urlInput, setUrlInput] = useState('');
    const [resolutionWarnings, setResolutionWarnings] = useState<Record<string, string>>({});
    const fileInputRef = useRef<HTMLInputElement>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            message.error('Invalid file format. Please upload an image (PNG, JPEG, WebP).');
            return;
        }

        const reader = new FileReader();
        reader.onload = async (event) => {
            const base64String = event.target?.result as string;
            const rawBase64 = base64String.split(',')[1] || base64String;

            const check = await checkResolution(base64String);
            const tempId = generateTempId();

            if (!check.isValid) {
                setResolutionWarnings(prev => ({
                    ...prev,
                    [tempId]: `Optimal resolution for Kiosk view is 1080x1920 (layout v1) or 1080x1300 (layout v2). Uploaded: ${check.width}x${check.height}.`
                }));
                message.warning(`Uploaded slide has sub-optimal resolution (${check.width}x${check.height}).`);
            }

            const newSlide: AttractorImageDto = {
                guid: tempId,
                imageBase64: rawBase64,
                imageUrl: null,
                fileName: file.name,
                contentType: file.type,
                sortOrder: images.length + 1,
                dateCreated: new Date().toISOString(),
                createdBy: currentUserGuid || 'System User'
            };

            onChange([...images, newSlide]);
            message.success(`Uploaded ${file.name} successfully.`);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleAddUrl = () => {
        if (!urlInput.trim()) {
            message.warning('Please enter a valid URL.');
            return;
        }

        if (!urlInput.startsWith('http://') && !urlInput.startsWith('https://')) {
            message.warning('URL must start with http:// or https://');
            return;
        }

        const tempId = generateTempId();
        const newSlide: AttractorImageDto = {
            guid: tempId,
            imageBase64: null,
            imageUrl: urlInput.trim(),
            sortOrder: images.length + 1,
            dateCreated: new Date().toISOString(),
            createdBy: currentUserGuid || 'System User'
        };

        onChange([...images, newSlide]);
        setUrlInput('');
        message.success('Added external URL slide.');
    };

    const handleDeleteSlide = (index: number) => {
        const updated = images.filter((_, idx) => idx !== index);
        updated.forEach((slide, idx) => {
            slide.sortOrder = idx + 1;
        });
        onChange(updated);
        message.info('Slide removed.');
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = images.findIndex((slide) => (slide.guid || slide.sortOrder?.toString()) === active.id);
        const newIndex = images.findIndex((slide) => (slide.guid || slide.sortOrder?.toString()) === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const updated = arrayMove(images, oldIndex, newIndex);
            updated.forEach((slide, idx) => {
                slide.sortOrder = idx + 1;
            });
            onChange(updated);
        }
    };

    return (
        <Card title="Slide Playlist" bodyStyle={{ padding: '24px' }} style={{ borderRadius: '8px' }}>
            <Paragraph>
                Manage the ordered list of slides. Drag the handles to reorder slides. Optimal dimensions for kiosk display are <strong>1080x1920</strong> (full layout v1) or <strong>1080x1300</strong> (partial layout v2).
            </Paragraph>

            {images.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', marginBottom: '24px' }}>
                    <ExclamationCircleOutlined style={{ fontSize: '32px', color: '#94a3b8', marginBottom: '12px' }} />
                    <p style={{ margin: 0, color: '#64748b' }}>No slides configured. Add local images or external URLs below.</p>
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    modifiers={[restrictToVerticalAxis]}
                >
                    <SortableContext
                        items={images.map((slide) => slide.guid || slide.sortOrder!.toString())}
                        strategy={verticalListSortingStrategy}
                    >
                        {images.map((slide, index) => (
                            <SortableSlideItem
                                key={slide.guid || slide.sortOrder}
                                id={slide.guid || slide.sortOrder!.toString()}
                                slide={slide}
                                index={index}
                                getUserName={getUserName}
                                handleDelete={handleDeleteSlide}
                                warningText={slide.guid ? resolutionWarnings[slide.guid] : undefined}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            )}

            <Divider style={{ margin: '24px 0' }} />

            <Title level={5} style={{ marginBottom: '16px' }}>Add Slide to Rotation</Title>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px' }}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept="image/*"
                        style={{ display: 'none' }}
                    />
                    <Button
                        type="dashed"
                        icon={<UploadOutlined />}
                        onClick={() => fileInputRef.current?.click()}
                        style={{ width: '100%', height: '50px' }}
                    >
                        Upload Local Image File
                    </Button>
                </div>

                <div style={{ flex: '2 1 400px', display: 'flex', gap: '8px' }}>
                    <Input
                        placeholder="Enter external image URL (https://...)"
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        style={{ flexGrow: 1 }}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddUrl}
                        style={{ height: '100%' }}
                    >
                        Add URL
                    </Button>
                </div>
            </div>
        </Card>
    );
};

export default AttractorSlideManager;
